import { useEffect, useRef, useState } from 'react'
import { PageHeader } from '../../../components/PageHeader'
import { WeeklySubmittedScreen } from '../../../components/WeeklySubmittedScreen'
import { IngredientCardImage } from '../components/IngredientCardImage'
import { getCardOrPlaceholder } from '../data/ingredientCards'
import {
  ensureMySubmission, saveMySubmission, withdrawMySubmission,
  weekRangeLabel,
} from '../../../lib/api/weekly'
import { supabase } from '../../../lib/supabase'
import type { WeeklyEventRow, WeeklySubmissionRow } from '../../../lib/database.types'
import { readError } from '../../../lib/errors'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { useT } from '../../../i18n'

const MAX_STORY = 600
const MAX_TITLE = 40

interface Props {
  eventId: string
  onBack: () => void
  onSubmitted: () => void
}

/**
 * Write / edit this week's dish — a single titled story built around
 * the shared + random ingredients. Auto-saves on blur of every field.
 */
export function SubmissionEditorScreen({ eventId, onBack, onSubmitted }: Props) {
  const t = useT()
  const [event, setEvent] = useState<WeeklyEventRow | null>(null)
  const [sub, setSub] = useState<WeeklySubmissionRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [title, setTitle] = useState('')
  const [story, setStory] = useState('')
  const [savingState, setSavingState] = useState<'idle' | 'saving' | 'saved'>('idle')
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)
  const discardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const ev = await supabase
          .from('weekly_events')
          .select('*')
          .eq('id', eventId)
          .single()
        if (ev.error) throw ev.error
        if (cancelled) return
        setEvent(ev.data as WeeklyEventRow)

        const mine = await ensureMySubmission(eventId)
        if (cancelled) return
        setSub(mine)
        setTitle(mine.title ?? '')
        setStory(mine.course1_text ?? '')
      } catch (e) {
        if (!cancelled) setError(readError(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [eventId])

  useEffect(() => () => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
  }, [])

  async function save(nextTitle: string, nextStory: string): Promise<boolean> {
    if (!sub) return false
    if (sub.is_final) {
      setError(t("This week is locked for voting. You can\'t edit it anymore."))
      return false
    }
    setSavingState('saving'); setError(null)
    try {
      const saved = await saveMySubmission(sub.id, nextTitle, nextStory)
      setSub(saved)
      setSavingState('saved')
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
      savedTimerRef.current = setTimeout(() => setSavingState('idle'), 1500)
      return true
    } catch (e) {
      setSavingState('idle')
      setError(readError(e))
      return false
    }
  }

  // ── Render ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="screen">
        <BackgroundTitle text={t("Weekly I'd Eat That")} color="#B58615" />
        <PageHeader title={t('Your Dish')} onBack={onBack} />
        <div className="flex-1 px-5 py-5 space-y-3">
          <div className="h-24 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="h-10 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="h-40 rounded-2xl bg-brand-elevated animate-pulse" />
        </div>
      </div>
    )
  }

  if (!sub || !event) {
    return (
      <div className="screen">
        <BackgroundTitle text={t("Weekly I'd Eat That")} color="#B58615" />
        <PageHeader title={t('Your Dish')} onBack={onBack} />
        <div className="flex-1 flex items-center justify-center px-5 text-center">
          <p className="text-sm text-brand-danger">{error ?? t('Could not load your submission.')}</p>
        </div>
      </div>
    )
  }

  const sharedCards = event.shared_ingredients.map(id => getCardOrPlaceholder(id))
  const randomCards = sub.random_ingredients.map(id => getCardOrPlaceholder(id))
  const titleFilled = title.trim().length > 0
  const storyFilled = story.trim().length > 10
  const ready = titleFilled && storyFilled
  const locked = sub.is_final
  // Only meaningful once a story has been finalised for voting — at
  // that point a re-edit would reset its votes. During the open
  // writing week the auto-save churn doesn't count: no votes exist
  // yet, so editing is harmless and we don't want to alarm a
  // first-time writer who's just typed their opening sentence.
  const previouslySubmitted = sub.is_final

  if (submitted) {
    return (
      <WeeklySubmittedScreen
        title={t("Weekly · I'd Eat That")}
        accent="#E8A33D"
        message={t("Your dish is entered in this week's event. Winners are revealed next Monday. Good luck!")}
        onBackToMenu={onSubmitted}
        onReview={locked ? undefined : () => setSubmitted(false)}
        reviewLabel={t('Review / edit my dish')}
      />
    )
  }

  return (
    <div className="screen">
      <BackgroundTitle text={t("Weekly I'd Eat That")} color="#B58615" />
      <PageHeader
        title={t('Your Dish')}
        subtitle={`${weekRangeLabel(event.week_start)} · ${locked ? t('Locked, voting is open') : 'auto-saves as you type'}`}
        onBack={onBack}
      />

      <div className="flex-1 flex flex-col px-5 py-5 gap-5 overflow-y-auto pb-36 relative z-10">
        {previouslySubmitted && !locked && (
          <div className="rounded-2xl border-2 border-brand-danger/40 bg-brand-danger/5 p-4">
            <p className="font-semibold text-brand-danger text-sm flex items-center gap-1.5">
              <span aria-hidden>⚠️</span> {t('Editing resets your votes')}
            </p>
            <p className="text-xs text-brand-text mt-1 leading-relaxed">{t('You already submitted this dish. If you save it again you’ll lose any votes it has already received and the new version will start from zero.')}</p>
          </div>
        )}

        {/* Name your dish */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-brand-muted uppercase tracking-wide
                          flex items-center gap-1.5">
              <span aria-hidden>🍽️</span> {t('Name your dish')}
            </p>
            <span className={`text-xs font-medium
              ${title.length > MAX_TITLE * 0.85
                ? 'text-brand-danger' : 'text-brand-muted'}`}>
              {title.length}/{MAX_TITLE}
            </span>
          </div>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value.slice(0, MAX_TITLE))}
            onBlur={() => save(title, story)}
            disabled={locked}
            maxLength={MAX_TITLE}
            placeholder={t("e.g. Grandma's Midnight Ramen")}
            className="w-full px-4 py-3 rounded-2xl border border-brand-border
                       bg-linear-to-b from-white to-brand-bg
                       text-brand-text placeholder:text-brand-muted/60
                       font-display text-lg font-bold tracking-tight
                       focus:outline-hidden focus:border-brand-primary focus:ring-2
                       focus:ring-brand-primary/20 transition-all
                       disabled:opacity-70 disabled:cursor-not-allowed"
          />
        </div>

        {/* Ingredients */}
        <div>
          <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
            {t('Your ingredients')}
          </p>
          <div className="flex flex-wrap gap-2">
            {sharedCards.map(card => card && (
              <IngredientCardImage key={card.id} id={card.id} size="sm" />
            ))}
            {randomCards.map(card => card && (
              <IngredientCardImage key={card.id} id={card.id} size="sm" />
            ))}
          </div>
          {randomCards.length > 0 && (
            <p className="text-xs text-brand-muted mt-2">
              Your bonus card:{' '}
              <span className="font-medium text-brand-text">
                {randomCards.map(c => c?.name).filter(Boolean).join(' + ')}
              </span>
            </p>
          )}
        </div>

        {/* Story textarea */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-brand-muted uppercase tracking-wide">
              {t('✍️ Describe your food story')}
            </p>
            <span className={`text-xs font-medium
              ${story.length > MAX_STORY * 0.85
                ? 'text-brand-danger' : 'text-brand-muted'}`}>
              {story.length}/{MAX_STORY}
            </span>
          </div>
          <p className="text-xs text-brand-muted mb-2 leading-relaxed">{t('You must use all of the ingredients above, and you cannot add any other edible ingredients. If you add anything else edible you may be disqualified from receiving votes. Tell a wild story! Not everything has to be a food dish, full creativity is encouraged.')}</p>
          <textarea
            value={story}
            onChange={e => setStory(e.target.value.slice(0, MAX_STORY))}
            onBlur={() => save(title, story)}
            rows={8}
            disabled={locked}
            placeholder={t('Start writing your dish story...')}
            className="w-full px-4 py-3 rounded-2xl border border-brand-border bg-white
                       text-brand-text placeholder:text-brand-muted/60 resize-none text-sm
                       focus:outline-hidden focus:border-brand-primary focus:ring-2
                       focus:ring-brand-primary/20 transition-all leading-relaxed
                       disabled:opacity-70 disabled:cursor-not-allowed"
          />
          <p className="text-xs text-brand-muted mt-1 h-4">
            {error
              ? <span className="text-brand-danger">{error}</span>
              : savingState === 'saving' ? 'Saving…'
              : savingState === 'saved'  ? t('Saved ✓')
              : ''}
          </p>
        </div>

        {!locked && (
          <button
            type="button"
            onClick={async () => {
              if (!confirmingDiscard) {
                setConfirmingDiscard(true)
                if (discardTimerRef.current) clearTimeout(discardTimerRef.current)
                discardTimerRef.current = setTimeout(() => setConfirmingDiscard(false), 3500)
                return
              }
              try {
                await withdrawMySubmission(sub.id)
                onBack()
              } catch (e) { setError(readError(e)) }
            }}
            className={`w-full py-3 text-sm rounded-2xl border transition-colors
                        ${confirmingDiscard
                          ? 'border-brand-danger text-white bg-brand-danger font-semibold'
                          : 'border-brand-border text-brand-muted bg-white'}`}
          >
            {confirmingDiscard ? t('Tap again to throw it out') : t('Throw out my entry')}
          </button>
        )}

        <div className="card p-4 bg-brand-elevated">
          <p className="text-xs font-semibold text-brand-text mb-1">{t('📌 Remember')}</p>
          <ul className="text-xs text-brand-muted space-y-1">
            <li>{t('• Edit any time this week. Saved automatically as you type.')}</li>
            <li>{t('• Next week, voting opens on this dish.')}</li>
            <li>{t('• Most likes wins and joins the Hall of Fame.')}</li>
          </ul>
        </div>
      </div>

      <div className="floating-actions">
        <button
          onClick={async () => { if (await save(title, story)) setSubmitted(true) }}
          disabled={!ready || locked}
          className="btn-iet disabled:opacity-40"
        >
          {locked
            ? '🔒 Locked. Go vote!'
            : !titleFilled
              ? t('Name your dish to submit')
              : !storyFilled
                ? t('Write a few more words to submit')
                : '✅ Save & Done'}
        </button>
      </div>
    </div>
  )
}
